/**
 * inventory-quantity.js
 *
 * When the inventory locations dialog opens, fetches live inventory from the proxy API,
 * shows a skeleton state during fetch, then updates only matching .inventory-locations__item
 * elements with the returned available counts. Runs inside the inventory-quantity custom
 * element; listens for dialog:open on the containing dialog-component.
 *
 * - When it runs: on dialog:open from the dialog-component that contains this element.
 * - DOM update: finds .inventory-locations__item[data-id="<id>"], then .inventory-locations__availability
 *   inside it; reads data-amount template (e.g. "XXX units available"), replaces XXX with available,
 *   updates text content only when available > 0.
 * - Failure: API errors or missing settings/variantId are caught; skeleton is always removed in finally,
 *   existing markup is left unchanged so the section is never stuck in loading state.
 */

(function () {
  'use strict';

  const DIALOG_OPEN = 'dialog:open';
  const SKELETON_CLASS = 'inventory-locations__list--skeleton';
  const PLACEHOLDER = 'XXX';

  /**
   * Resolves the current variant ID from the product section (form input name="id").
   * @param {HTMLElement} root - Element to start from (inventory-quantity).
   * @returns {string|null}
   */
  function getVariantId(root) {
    const section = root.closest && root.closest('.shopify-section');
    if (!section) return null;
    const input = section.querySelector && section.querySelector('input[name="id"]');
    return input && input.value ? String(input.value).trim() || null : null;
  }

  /**
   * Updates only matching list items from API response. For each item with available > 0,
   * finds .inventory-locations__item[data-id="id"], then .inventory-locations__availability,
   * reads data-amount (template with XXX), replaces XXX with available, sets text content.
   * Preserves the check icon if present for in-stock states; for zero stock it shows the
   * "not available" label and updates class state.
   * @param {HTMLElement} listEl - The inventory-quantity element (list wrapper).
   * @param {Array<{ id: string, title?: string, available: number }>} items
   */
  function updateDom(listEl, items) {
    if (!listEl || !Array.isArray(items)) return;
    const notAvailableLabel =
      (listEl.dataset && listEl.dataset.notAvailableLabel ? String(listEl.dataset.notAvailableLabel).trim() : '') || 'Not available';

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const id = item && item.id;
      const available = typeof item.available === 'number' ? item.available : 0;
      if (id == null) continue;

      const row = listEl.querySelector && listEl.querySelector('.inventory-locations__item[data-id="' + id + '"]');
      if (!row) continue;

      const availabilityEl = row.querySelector && row.querySelector('.inventory-locations__availability');
      if (!availabilityEl) continue;

      if (available > 0) {
        const template = availabilityEl.getAttribute && (availabilityEl.getAttribute('data-amount') || availabilityEl.getAttribute('data-unit'));
        if (!template || template.indexOf(PLACEHOLDER) === -1) continue;

        const text = template.replace(PLACEHOLDER, String(available));
        var icon = availabilityEl.querySelector && availabilityEl.querySelector('.inventory-locations__check');
        availabilityEl.textContent = text;
        if (icon) availabilityEl.insertBefore(icon, availabilityEl.firstChild);
        availabilityEl.classList.remove('inventory-locations__availability--none');
      } else {
        availabilityEl.textContent = notAvailableLabel;
        availabilityEl.classList.add('inventory-locations__availability--none');
      }
    }
  }

  /**
   * Adds skeleton class to the list wrapper.
   */
  function addSkeleton(el) {
    if (el && el.classList) el.classList.add(SKELETON_CLASS);
  }

  /**
   * Removes skeleton class so content is always visible after fetch (success or failure).
   */
  function removeSkeleton(el) {
    if (el && el.classList) el.classList.remove(SKELETON_CLASS);
  }

  class InventoryQuantity extends HTMLElement {
    constructor() {
      super();
      /** @type {AbortController | null} */
      this._abortController = null;
      /** @type {HTMLElement | null} */
      this._dialogComponent = null;
      /** @type {(event: Event) => void} */
      this._boundOnDialogOpen = this._onDialogOpen.bind(this);
    }

    connectedCallback() {
      const dialogComponent = this.closest && this.closest('dialog-component');
      if (!dialogComponent || !dialogComponent.addEventListener) return;
      this._dialogComponent = dialogComponent;
      dialogComponent.addEventListener(DIALOG_OPEN, this._boundOnDialogOpen);
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
      if (this._dialogComponent && this._boundOnDialogOpen) {
        this._dialogComponent.removeEventListener(DIALOG_OPEN, this._boundOnDialogOpen);
        this._dialogComponent = null;
      }
      removeSkeleton(this);
    }

    /**
     * Runs when the inventory dialog is opened. Shows skeleton, fetches proxy API with current
     * variantId, then updates matching items and always removes skeleton (success or failure).
     * No-op if api_proxy or variantId is missing; does not throw.
     */
    _onDialogOpen(event) {
      if (!this._dialogComponent || event.target !== this._dialogComponent) return;

      const apiProxy = (this.dataset && this.dataset.apiProxy) ? String(this.dataset.apiProxy).trim() : '';
      const variantId = getVariantId(this);

      if (!apiProxy || !variantId) {
        removeSkeleton(this);
        return;
      }

      if (this._abortController) {
        this._abortController.abort();
      }
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      addSkeleton(this);

      const url = apiProxy.replace(/\/?$/, '') + '/product/' + encodeURIComponent(variantId) + '/inventory';

      fetch(url, { signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Inventory API error');
          return res.json();
        })
        .then(
          function (data) {
            if (signal.aborted) return;
            const items = Array.isArray(data) ? data : [];
            updateDom(this, items);
          }.bind(this)
        )
        .catch(function () {
          /* On failure leave existing markup unchanged; no throw. */
        })
        .finally(
          function () {
            if (this._abortController === null) return;
            this._abortController = null;
            removeSkeleton(this);
          }.bind(this)
        );
    }
  }

  if (!customElements.get('inventory-quantity')) {
    customElements.define('inventory-quantity', InventoryQuantity);
  }
})();
